<template>
	<form v-if="!isSuccess" @submit.prevent>
		<div class="grid-md gutters-100-md">
			<div class="col-6-md">
				<InputField :field="v$.name" field-name="Name">
					<template #label>Name</template>
					<template #input>
						<input v-model="v$.name.$model" data-test="name" name="name" class="input" type="text" />
					</template>
				</InputField>
			</div>
			<div class="col-6-md">
				<InputField :field="v$.email" field-name="Email Address">
					<template #label>Email address</template>
					<template #input>
						<input
							v-model="v$.email.$model"
							data-test="email"
							name="email"
							class="input"
							type="email"
							@clear-server="serverValidationHandler.clearServerErrors('email')"
						/>
					</template>
				</InputField>
			</div>
		</div>
		<div class="my-100">
			<InputField :field="v$.message" field-name="Message">
				<template #label>Message</template>
				<template #input>
					<textarea
						v-model="v$.message.$model"
						data-test="message"
						name="message"
						class="input"
						type="text"
						rows="5"
					/>
				</template>
			</InputField>
		</div>
		<BaseButtonGroup>
			<template #primary>
				<SubmitButton :is-working="isSubmitted" data-test="contact-form-submit" :v$="v$" @sb-click="submit">
					Send message
				</SubmitButton>
			</template>
		</BaseButtonGroup>
	</form>
	<BaseAlert
		v-else
		:alert="{
			message: [
				`Thank you for contacting us. A member of our ${contactTeam} team will reach out to you shortly.`
			],
			type: 'success'
		}"
		class="mt-100 mt-200-md"
		data-test="submit-success-alert"
	/>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from 'vue';
import { email, maxLength, required } from '@vuelidate/validators';
import { sendConnectApiContactRequest, sendContactRequest } from '@api/contact';
import { ContactForm } from 'types/contact';
import { getDistinctId } from '@utils/analytics';
import InputField from '@components/form/input-field.vue';
import { nameRegex } from '@validators/nameRegex';
import ServerValidationHandler from '@classes/ServerValidationHandler';
import SubmitButton from '@components/form/submit-button.vue';
import { useAppStore } from '@stores/app';
import { useRoute } from 'vue-router';
import { useVuelidate } from '@vuelidate/core';

interface ContactUsFormState {
	name: string;
	email: string;
	message: string;
}

interface Props {
	isConnectApi?: boolean;
}

interface Emits {
	(e: 'submit-success'): void;
}

const props = withDefaults(defineProps<Props>(), {
	isConnectApi: false
});

const emits = defineEmits<Emits>();

const appStore = useAppStore();

const maxMessageLength = 1000;
const serverValidationHandler = ref(new ServerValidationHandler());

const state: ContactUsFormState = reactive({
	name: '',
	email: '',
	message: ''
});

const rules = computed(() => ({
	name: { required, nameRegex },
	email: { required, email, serverError: serverValidationHandler.value.serverError('email') },
	message: { required, maxLength: maxLength(maxMessageLength) }
}));

const v$ = useVuelidate(rules, state);

const route = useRoute();

const isSubmitted = ref(false);
const isSuccess = ref(false);
const contactForm: ContactForm = reactive({
	clientId: `${import.meta.env.VITE_API_CLIENT_ID}`,
	email: state.email,
	mixpanelId: getDistinctId() || ''
});

const isAdvisorUser = computed(
	(): boolean => appStore.isAdvisor || !!route.query?.isAdvisor || route.query?.isAdvisor === 'true'
);

const contactTeam = computed((): string => (props.isConnectApi ? 'Connect API' : 'Investor Relations'));

async function handleSubmitFailure(): Promise<void> {
	isSubmitted.value = false;

	appStore.addToast({
		type: 'error',
		message:
			'An error has occurred. The error has been logged and we&rsquo;ll look into it immediately. If you need assistance, contact us at&nbsp;<a href="mailto:support@fundrise.com">support@fundrise.com</a>.'
	});
}

async function submit(): Promise<void> {
	isSubmitted.value = true;

	const fullName = state.name.split(' ');
	const firstName = fullName.splice(0, 1).toString();
	let lastName = '';

	if (fullName.length > 0) {
		lastName = fullName.join(' ');
	}

	try {
		if (props.isConnectApi) {
			await sendConnectApiContactRequest(
				firstName,
				lastName,
				state.email,
				state.message,
				contactForm.mixpanelId,
				contactForm.clientId,
				isAdvisorUser.value
			);
		} else {
			await sendContactRequest(
				firstName,
				lastName,
				state.email,
				state.message,
				contactForm.mixpanelId,
				contactForm.clientId,
				isAdvisorUser.value
			);
		}

		isSuccess.value = true;
		emits('submit-success');
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		serverValidationHandler.value.handleServerValidation(error);

		await handleSubmitFailure();
	}
}
</script>
